import { useCallback, useMemo } from 'react'
import { useSnapshotStore } from './snapshot.store.hook'
import { SnapshotDto } from './snapshot.dto'
import {
  Snapshot,
  SnapshotContribution,
  SnapshotPerspective,
} from './snapshot.factory'

import { ContributionFilter } from '../../screens/contributions/types'

const contributionFilter = (filter: ContributionFilter) => {
  return (contribution: SnapshotContribution) => {
    return (
      (filter.status === 'All' || filter.status === contribution.status) &&
      (filter.type === 'All' || filter.type === contribution.type)
    )
  }
}
export const useReplayApi = () => {
  const { snapshot } = useSnapshotStore()

  const getClusterList = useCallback(
    (searchQuery: string = '') => {
      if (!snapshot.node.id) {
        return []
      }
      const clusters = snapshot.node.clusters.toReversed().filter((cluster) => {
        if (!searchQuery) {
          return true
        }
        return cluster.topic.toLowerCase().includes(searchQuery.toLowerCase())
      })
      return clusters
        .map((cluster) => {
          return SnapshotDto.availableClusters(cluster)
        })
        .sort((a, b) => {
          return (
            new Date(b.contentUpdatedAt).getTime() -
            new Date(a.contentUpdatedAt).getTime()
          )
        })
    },
    [snapshot]
  )

  const getClusterContributions = useCallback(
    (clusterId: string, filter: ContributionFilter) => {
      if (!snapshot.node.id) {
        return []
      }
      const cluster = snapshot.node.clusters
        .toReversed()
        .find((cluster) => cluster.id === clusterId)
      if (!cluster) {
        return []
      }
      return cluster.perspectives
        .flatMap((perspective) => perspective.contributions)
        .filter(contributionFilter(filter))
        .map((contribution) => {
          return SnapshotDto.contribution(contribution)
        })
    },
    [snapshot]
  )

  const getClusters = useCallback(() => {
    if (!snapshot.node.id) {
      return []
    }

    return snapshot.node.clusters.toReversed().map((cluster) => {
      return SnapshotDto.cluster(cluster)
    })
  }, [snapshot])

  const getClusterPerspectives = useCallback(
    (clusterId: string) => {
      if (!snapshot.node.id) {
        return []
      }
      const cluster = snapshot.node.clusters.find(
        (cluster) => cluster.id === clusterId
      )
      if (!cluster) {
        return []
      }
      return cluster.perspectives
        .map((perspective) => {
          return SnapshotDto.perspective(perspective)
        })
        .sort((a, b) => {
          if (a.voteCount === b.voteCount) {
            return b.contributionCount - a.contributionCount
          }
          return b.voteCount - a.voteCount
        })
    },
    [snapshot]
  )

  const getPerspective = useCallback(
    (perspectiveId: string) => {
      if (!snapshot.node.id) {
        return null
      }
      const snapshotHelper = new Snapshot(snapshot)
      const perspective = snapshotHelper.getNode(
        perspectiveId
      ) as SnapshotPerspective | null

      if (!perspective) {
        return null
      }
      return SnapshotDto.perspective(perspective)
    },

    [snapshot]
  )

  const getPerspectiveContributions = useCallback(
    (perspectiveId: string, filter: ContributionFilter) => {
      const snapshotHelper = new Snapshot(snapshot)
      const perspective = snapshotHelper.getNode(
        perspectiveId
      ) as SnapshotPerspective | null

      if (!perspective) {
        return []
      }
      return perspective.contributions
        .filter(contributionFilter(filter))
        .map((contribution) => {
          return SnapshotDto.contribution(contribution)
        })
    },
    [snapshot]
  )

  const getContribution = useCallback(
    (contributionId: string) => {
      const snapshotHelper = new Snapshot(snapshot)
      const contribution = snapshotHelper.getNode(
        contributionId
      ) as SnapshotContribution | null

      if (!contribution) {
        return null
      }
      return SnapshotDto.contribution(contribution)
    },
    [snapshot]
  )

  const getContributionBumps = useCallback(
    (contributionId: string) => {
      const snapshotHelper = new Snapshot(snapshot)
      const contribution = snapshotHelper.getNode(
        contributionId
      ) as SnapshotContribution | null

      return contribution?.bumps.map(SnapshotDto.contributionBump) || []
    },
    [snapshot]
  )

  const clusterService = useMemo(() => {
    return {
      getClusterList,
      getClusters,
      getClusterContributions,
      getClusterPerspectives,
    }
  }, [
    getClusterList,
    getClusters,
    getClusterContributions,
    getClusterPerspectives,
  ])

  const perspectiveService = useMemo(() => {
    return {
      getPerspective,
      getPerspectiveContributions,
    }
  }, [getPerspective, getPerspectiveContributions])

  const contributionService = useMemo(() => {
    return {
      getContribution,
      getContributionBumps,
    }
  }, [getContribution, getContributionBumps])

  return {
    clusterService,
    perspectiveService,
    contributionService,
    tick: snapshot.tick,
    nodeId: snapshot.node.id,
  }
}
